<template>
  <Modal
    redirect-on-close
    :redirect-route="{ name: 'admin.products.packages.package.packageItems' }"
  >
    <div class="modal max-w-md mx-auto" slot-scope="{ closeModal }" @click.stop>
      <div class="modal__header">
        <span class="block text-xs text-gray-700 uppercase tracking-wider">Package Item</span>
        <h3 class="text-2xl text-gray-900 leading-tight">Τροποποίηση {{ packageItem.name }}</h3>
      </div>
      <div class="modal__body">
        <ValidationObserver slim v-slot="{ handleSubmit, invalid }" ref="observer">
          <form class="space-y-16" @submit.prevent="handleSubmit(submit)">
            <div class="space-y-8">
              <div class="space-y-2">
                <label for="productId" class="label"> Product * </label>
                <ValidationProvider
                  name="item"
                  vid="productId"
                  rules="required"
                  v-slot="{ errors, failed }"
                  slim
                >
                  <VMultiselect
                    v-model="computedProduct"
                    label="name"
                    track-by="id"
                    placeholder="Select product/service"
                    open-direction="bottom"
                    :disabled="updating || fetching"
                    :options="products"
                    :searchable="true"
                    :clear-on-select="true"
                    :close-on-select="true"
                    :options-limit="300"
                    :max-height="400"
                    :hide-selected="true"
                  >
                    <template slot="option" slot-scope="props">
                      <p class="mb-2">
                        {{ props.option.name }}
                      </p>
                      <span
                        v-if="props.option.type === 'service'"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"
                      >
                        Service
                      </span>
                      <span
                        v-if="props.option.type === 'basic'"
                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        Basic
                      </span>
                    </template>
                  </VMultiselect>
                  <p v-if="failed" class="msg error">
                    {{ errors[0] }}
                  </p>
                </ValidationProvider>
              </div>
              <div v-if="packageItemData.productId" class="space-y-2">
                <label for="name" class="label capitalize"> Name * </label>
                <ValidationProvider
                  name="name"
                  vid="name"
                  rules="required"
                  v-slot="{ errors, failed }"
                  slim
                >
                  <input
                    id="name"
                    v-model="packageItemData.name"
                    :disabled="updating || fetching"
                    :class="{ 'has-error': failed }"
                    type="text"
                    class="input"
                    placeholder="enter name..."
                  />
                  <p v-if="failed" class="msg error">
                    {{ errors[0] }}
                  </p>
                </ValidationProvider>
              </div>
              <div class="flex-none">
                <label class="label custom-checkbox">
                  <input
                    type="checkbox"
                    @input="setOptional"
                    :disabled="updating || fetching"
                    :checked="packageItemData.optional"
                  />
                  <span />
                  <span>Optional</span>
                </label>
              </div>
              <div v-show="packageItemData.optional" key="packageItemPrice" class="space-y-2">
                <label for="price" class="label capitalize"> Price * </label>
                <ValidationProvider
                  name="price"
                  vid="price"
                  :rules="priceRules"
                  v-slot="{ errors, failed }"
                  slim
                >
                  <input
                    id="price"
                    v-model="packageItemData.price"
                    :disabled="updating || fetching"
                    :class="{ 'has-error': failed }"
                    type="number"
                    step="0.01"
                    class="input w-32"
                    placeholder="enter price..."
                  />
                  <p v-if="failed" class="msg error">
                    {{ errors[0] }}
                  </p>
                </ValidationProvider>
              </div>
            </div>
            <div class="space-x-2 text-right">
              <BaseButton type="button" ghost :disabled="updating || fetching" @click="closeModal">
                Cancel
              </BaseButton>
              <BaseButton type="submit" primary :disabled="updating || fetching || invalid">
                Submit
              </BaseButton>
            </div>
          </form>
        </ValidationObserver>
      </div>
    </div>
  </Modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Modal from '@/components/Ui/Modal.vue';

export default {
  name: 'ModalEdit',
  components: {
    Modal,
  },
  props: {
    updating: Boolean,
    product: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    fetching: false,
    packageItemData: {
      name: '',
      productId: null,
      optional: false,
      price: 0,
      requirements: null,
    },
  }),
  computed: {
    ...mapGetters('admin/products', ['products']),
    ...mapGetters('admin/packageItems', ['packageItems', 'packageItem']),
    computedProduct: {
      get() {
        if (!this.packageItemData.productId) {
          return null;
        }
        return this.$store.state.entities.products.byId[this.packageItemData.productId] || null;
      },
      set(product) {
        this.packageItemData.productId = product?.id || null;
      },
    },
    priceRules() {
      const { optional } = this.packageItemData;
      return `${optional ? 'required|' : ''}decimal:2|min_value:0`;
    },
  },
  created() {
    this.fetching = true;
    this.$store
      .dispatch('admin/products/fetchProductList', {
        params: {
          limit: 100,
          filter: {
            type: 'basic,service',
          },
        },
      })
      .then(() => {
        this.fetching = false;
      })
      .catch(() => {
        this.fetching = false;
      });
  },
  mounted() {
    this.packageItemData = { ...this.packageItem };
  },
  methods: {
    ...mapActions('admin/products', ['fetchBasicProductList']),
    setOptional(ev) {
      const { checked } = ev.target;
      this.packageItemData.optional = checked;
    },
    submit() {
      this.$emit('submit', { ...this.packageItemData });
    },
    backToPackageItems() {
      this.$router.replace({ name: 'admin.products.packages.package.packageItems' });
    },
  },
};
</script>
